<template>
  <v-container fluid class="pa-0">
    <!-- Page Header -->
    <v-card flat class="mb-3 elevation-2">
      <v-toolbar color="primary" flat>
        <v-icon left color="black">map</v-icon>
        <v-toolbar-title class="black--text font-weight-medium">
          Rider Tracker
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-chip :color="loading ? 'orange' : 'green'" text-color="white" small>
          <v-avatar left>
            <v-icon small>{{ loading ? "refresh" : "check_circle" }}</v-icon>
          </v-avatar>
          {{ loading ? "Loading..." : `${ridersList.length} Riders` }}
        </v-chip>
      </v-toolbar>
    </v-card>

    <v-layout row wrap>
      <!-- Statistics Panel -->
      <v-flex xs12 md4 lg3 class="px-1">
        <v-card class="mb-4" elevation="2">
          <v-card-title class="primary black--text py-3">
            <v-icon left color="black">assessment</v-icon>
            <span class="subtitle-1">Rider on map</span>
          </v-card-title>
          <v-card-text class="pa-0">
            <v-list dense>
              <v-list-tile>
                <v-list-tile-content>
                  <v-list-tile-title class="body-2"
                    >Total Riders</v-list-tile-title
                  >
                </v-list-tile-content>
                <v-list-tile-action>
                  <v-chip
                    small
                    color="blue lighten-4"
                    text-color="blue darken-2"
                  >
                    {{ ridersList.length }}
                  </v-chip>
                </v-list-tile-action>
              </v-list-tile>
              <v-divider></v-divider>
              <v-list-tile>
                <v-list-tile-content>
                  <v-list-tile-title class="body-2"
                    >Stale Locations</v-list-tile-title
                  >
                </v-list-tile-content>
                <v-list-tile-action>
                  <v-chip
                    small
                    color="deep-orange lighten-4"
                    text-color="deep-orange darken-2"
                  >
                    {{ staleRidersCount }}
                  </v-chip>
                </v-list-tile-action>
              </v-list-tile>
              <v-divider></v-divider>
              <v-list-tile>
                <v-list-tile-content>
                  <v-list-tile-title class="body-2"
                    >Available Riders</v-list-tile-title
                  >
                </v-list-tile-content>
                <v-list-tile-action>
                  <v-chip
                    small
                    color="green lighten-4"
                    text-color="green darken-2"
                  >
                    {{ activeRidersCount }}
                  </v-chip>
                </v-list-tile-action>
              </v-list-tile>
              <v-divider></v-divider>
              <v-list-tile>
                <v-list-tile-content>
                  <v-list-tile-title class="body-2"
                    >On Process</v-list-tile-title
                  >
                </v-list-tile-content>
                <v-list-tile-action>
                  <v-chip
                    small
                    color="orange lighten-4"
                    text-color="orange darken-2"
                  >
                    {{ onTheWayRidersCount }}
                  </v-chip>
                </v-list-tile-action>
              </v-list-tile>
              <v-divider></v-divider>
              <v-list-tile>
                <v-list-tile-content>
                  <v-list-tile-title class="body-2"
                    >Off Duty Riders</v-list-tile-title
                  >
                </v-list-tile-content>
                <v-list-tile-action>
                  <v-chip small color="red lighten-4" text-color="red darken-2">
                    {{ inactiveRidersCount }}
                  </v-chip>
                </v-list-tile-action>
              </v-list-tile>
            </v-list>
          </v-card-text>
        </v-card>

        <!-- Province Selection -->
        <v-card elevation="2" class="mb-4">
          <v-card-title class="primary black--text py-3">
            <v-icon left color="black">location_city</v-icon>
            <span class="subtitle-1">Quick Navigation</span>
          </v-card-title>
          <v-card-text class="pa-3">
            <v-autocomplete
              v-model="selectedProvince"
              :items="provinceOptions"
              label="Select Province"
              prepend-icon="my_location"
              @change="handleProvinceChange"
              clearable
              dense
              outlined
            ></v-autocomplete>

            <div class="mt-3">
              <v-subheader class="pa-0 mb-2">Popular Locations</v-subheader>
              <v-layout wrap>
                <v-flex
                  v-for="province in popularProvinces"
                  :key="province"
                  xs6
                  class="pa-1"
                >
                  <v-btn
                    @click="setCenter(province)"
                    :color="
                      selectedProvince === province
                        ? 'primary'
                        : 'grey lighten-3'
                    "
                    :class="selectedProvince === province ? 'black--text' : ''"
                    small
                    block
                    class="text-capitalize"
                  >
                    {{ province }}
                  </v-btn>
                </v-flex>
              </v-layout>
            </div>
          </v-card-text>
        </v-card>

        <!-- Rider Selection -->
        <v-card elevation="2">
          <v-card-title class="primary black--text py-3">
            <v-icon left color="black">person_pin</v-icon>
            <span class="subtitle-1">Select Rider</span>
          </v-card-title>
          <v-card-text class="pa-3">
            <v-autocomplete
              v-model="selectedRiderForWarp"
              :items="riderOptions"
              label="Select Rider to Track"
              prepend-icon="search"
              @change="warpToRider"
              clearable
              dense
              outlined
              :disabled="!ridersList.length"
              :loading="loading"
            >
              <template v-slot:item="{ item }">
                <v-list-tile-avatar>
                  <v-icon :color="getRiderStatusColor(item.status)">
                    {{ getRiderStatusIcon(item.status) }}
                  </v-icon>
                </v-list-tile-avatar>
                <v-list-tile-content>
                  <v-list-tile-title>{{ item.text }}</v-list-tile-title>
                  <v-list-tile-sub-title>{{
                    item.subtitle
                  }}</v-list-tile-sub-title>
                </v-list-tile-content>
              </template>
              <template v-slot:selection="{ item }">
                <v-chip
                  :color="getRiderStatusColor(item.status)"
                  text-color="white"
                  small
                >
                  <v-avatar left>
                    <v-icon small>person</v-icon>
                  </v-avatar>
                  {{ item.text }}
                </v-chip>
              </template>
            </v-autocomplete>

            <div v-if="selectedRiderInfo" class="mt-3">
              <v-card outlined>
                <v-card-text class="py-2">
                  <div class="body-2 font-weight-medium mb-1">
                    {{ selectedRiderInfo.name }}
                  </div>
                  <div class="caption grey--text">
                    ID: {{ selectedRiderInfo.riderId }}
                  </div>
                  <div class="caption grey--text">
                    Status:
                    <span
                      :class="
                        getRiderStatusColor(selectedRiderInfo.status) + '--text'
                      "
                    >
                      {{ statusDisplay(selectedRiderInfo.status) }}
                    </span>
                  </div>
                  <div class="caption grey--text mt-1">
                    Last Updated:
                    {{ formatDate(selectedRiderInfo.lastUpdated) }}
                  </div>
                  <div
                    class="caption mt-1"
                    :class="
                      selectedRiderInfo.locationStale
                        ? 'deep-orange--text text--darken-2'
                        : 'green--text text--darken-2'
                    "
                  >
                    Freshness:
                    {{ formatFreshness(selectedRiderInfo) }}
                  </div>
                </v-card-text>
              </v-card>
            </div>
          </v-card-text>
        </v-card>
      </v-flex>

      <!-- Map Container -->
      <v-flex xs12 md8 lg9 class="px-1">
        <v-card elevation="3">
          <v-card-title class="primary black--text py-3">
            <v-icon left color="black">map</v-icon>
            <span class="subtitle-1">Live Rider Locations</span>
            <v-spacer></v-spacer>
            <v-btn icon @click="refreshRiders" :loading="loading">
              <v-icon color="black">refresh</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text class="pa-0" style="position: relative">
            <div id="map" ref="mapRef" style="height: 600px; width: 100%">
              <div v-if="loading" class="loading-overlay">
                <v-progress-circular
                  indeterminate
                  size="64"
                  color="primary"
                ></v-progress-circular>
                <div class="mt-3 text-center">
                  <span class="subtitle-1">Loading riders...</span>
                </div>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import realtimeDispatchSocket from "@/services/realtimeDispatchSocket";
import appConfigFeatureService from "@/services/appConfigFeatureService";

export default {
  data() {
    return {
      map: null,
      markers: new Map(),
      snapshotMissCounts: new Map(),
      snapshotMissThreshold: 3,
      mapLoaded: false,
      loading: true,
      viewportTimerId: null,
      snapshotRefreshTimerId: null,
      hasReceivedInitialSnapshot: false,
      trackerSocketSubscription: null,
      trackerSocketState: "connecting",
      isModalVisible: false,
      selectedProvince: null,
      formData: {
        riderId: "",
        latitude: null,
        longitude: null,
      },
      newRider: {
        riderId: "",
        name: "",
      },
      ridersList: [],
      selectedRiderId: "",
      selectedRiderForWarp: null,
      selectedRiderInfo: null,
      popularProvinces: [
        "phuket",
        "trang",
        "phangnga",
        "songkhla",
        "phetchaburi",
      ],
      locations: {
        thailand: { lat: 13.7563, lng: 100.5018, zoom: 6 }, // Bangkok coordinates as default center
      },
      provinceCoordinates: {
        phuket: { lat: 7.8804, lng: 98.3923, zoom: 12 },
        suratthani: { lat: 9.1351, lng: 99.3332, zoom: 11 },
        krabi: { lat: 8.0863, lng: 98.9063, zoom: 11 },
        trang: { lat: 7.5645, lng: 99.6239, zoom: 11 },
        songkhla: { lat: 7.1896, lng: 100.5953, zoom: 11 },
        yala: { lat: 6.5413, lng: 101.2803, zoom: 11 },
        prachuapkhirikhan: { lat: 11.8126, lng: 99.7957, zoom: 11 },
        nakhonsithammarat: { lat: 8.4304, lng: 99.9633, zoom: 11 },
        pattani: { lat: 6.8692, lng: 101.255, zoom: 11 },
        ranong: { lat: 9.9529, lng: 98.6085, zoom: 11 },
        phatthalung: { lat: 7.6166, lng: 100.0742, zoom: 11 },
        satun: { lat: 6.6238, lng: 100.0675, zoom: 11 },
        phangnga: { lat: 8.4511, lng: 98.5292, zoom: 11 },
        phetchaburi: { lat: 13.1119, lng: 99.9406, zoom: 11 },
        ratchaburi: { lat: 13.5282, lng: 99.8134, zoom: 11 },
        chiangmai: { lat: 18.7883, lng: 98.9853, zoom: 11 },
        chiangrai: { lat: 19.9105, lng: 99.8403, zoom: 11 },
        pathumthani: { lat: 14.0208, lng: 100.5253, zoom: 11 },
        narathiwat: { lat: 6.4215, lng: 101.8235, zoom: 11 },
        lamphun: { lat: 18.5743, lng: 99.0087, zoom: 11 },
        buriram: { lat: 14.9951, lng: 103.1116, zoom: 11 },
      },
      googleMapsLoaded: false,
    };
  },
  computed: {
    isRiderTrackerEnabled() {
      return appConfigFeatureService.isFeatureEnabled("rider_tracker", {
        defaultValue: true,
      });
    },
    // Available: no order but ready to get order
    activeRidersCount() {
      return this.ridersList.filter((rider) => {
        return this.normalizeStatus(rider.status) === "active";
      }).length;
    },
    // On Process: currently handling an order
    onTheWayRidersCount() {
      return this.ridersList.filter((rider) => {
        return this.normalizeStatus(rider.status) === "on_the_way";
      }).length;
    },
    // Off Duty: signout or unavailable
    inactiveRidersCount() {
      return this.ridersList.filter((rider) => {
        return this.normalizeStatus(rider.status) === "inactive";
      }).length;
    },
    staleRidersCount() {
      return this.ridersList.filter((rider) => rider.locationStale).length;
    },
    provinceOptions() {
      return Object.keys(this.provinceCoordinates).map((province) => ({
        text: province.charAt(0).toUpperCase() + province.slice(1),
        value: province,
      }));
    },
    riderOptions() {
      return this.ridersList.map((rider) => ({
        text: rider.name || `Rider ${rider.riderId}`,
        value: rider.riderId,
        subtitle: `ID: ${rider.riderId} • ${this.statusDisplay(rider.status)}`,
        status: (rider.status || "").toLowerCase(),
        rider: rider,
      }));
    },
  },
  created() {},
  async mounted() {
    if (!this.isRiderTrackerEnabled) {
      this.$router.replace("/404");
      return;
    }
    await this.loadGoogleMaps();
    this.initRealtimeSocket();
    this.initMap();
  },
  beforeDestroy() {
    if (this.viewportTimerId) {
      clearTimeout(this.viewportTimerId);
      this.viewportTimerId = null;
    }
    if (this.snapshotRefreshTimerId) {
      clearInterval(this.snapshotRefreshTimerId);
      this.snapshotRefreshTimerId = null;
    }
    if (this.trackerSocketSubscription) {
      this.trackerSocketSubscription.dispose();
      this.trackerSocketSubscription = null;
    }
    this.snapshotMissCounts.clear();
  },
  methods: {
    normalizeStatus(status) {
      const s = (status || "").toLowerCase();
      if (s === "idle" || s === "online") return "active";
      if (s === "busy") return "on_the_way";
      if (s === "offline") return "inactive";
      if (s === "on_process" || s === "on-the-way") return "on_the_way";
      return s;
    },
    isAvailable(status) {
      return this.normalizeStatus(status) === "active";
    },
    isOnTheWay(status) {
      const s = this.normalizeStatus(status);
      return s === "on_the_way";
    },
    isInactive(status) {
      const s = this.normalizeStatus(status);
      return s !== "active" && s !== "on_the_way";
    },
    loadGoogleMaps() {
      return new Promise((resolve, reject) => {
        if (window.google && window.google.maps) {
          this.googleMapsLoaded = true;
          resolve();
          return;
        }

        const script = document.createElement("script");
        const apiKey = process.env.VUE_APP_GOOGLE_MAPS_API_KEY;
        if (!apiKey) {
          console.error(
            "Missing Google Maps API key. Set VUE_APP_GOOGLE_MAPS_API_KEY in your .env file."
          );
          reject(new Error("Missing Google Maps API key"));
          return;
        }
        script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=marker&v=beta`;
        script.async = true;
        script.defer = true;

        script.onload = () => {
          this.googleMapsLoaded = true;
          resolve();
        };

        script.onerror = (err) => {
          reject(new Error("Failed to load Google Maps"));
        };

        document.head.appendChild(script);
      });
    },

    async initMap() {
      if (!this.googleMapsLoaded) {
        console.error("Google Maps not loaded");
        return;
      }

      const initialCenter = this.locations.thailand;

      this.map = new google.maps.Map(this.$refs.mapRef, {
        center: initialCenter,
        zoom: initialCenter.zoom,
        mapId: "DEMO_MAP_ID",
        styles: [
          {
            featureType: "poi",
            elementType: "labels",
            stylers: [{ visibility: "off" }],
          },
        ],
      });

      await new Promise((resolve) => {
        this.map.addListener("tilesloaded", () => {
          this.mapLoaded = true;
          resolve();
        });
      });

      this.map.addListener("idle", () => {
        this.scheduleViewportRefresh();
      });

      this.requestRiders({ forceFullSnapshot: true });
    },

    initRealtimeSocket() {
      if (!this.isRiderTrackerEnabled) {
        this.loading = false;
        return;
      }
      const gatewayBase =
        process.env.VUE_APP_REALTIME_GATEWAY_URL ||
        process.env.VUE_APP_WEBSOCKET_TRACKER_URL ||
        "";
      const token = localStorage.getItem("token") || "";

      if (!gatewayBase || !token) {
        this.loading = false;
        return;
      }

      if (!this.trackerSocketSubscription) {
        this.trackerSocketSubscription = realtimeDispatchSocket.subscribe({
          onStateChange: (state) => {
            this.trackerSocketState = state;
            if (state === "connected") {
              this.requestRiders({
                forceFullSnapshot: !this.hasReceivedInitialSnapshot,
              });
            }
          },
          onEvent: (event) => {
            this.handleTrackerSocketEvent(event);
          },
        });
      }

      realtimeDispatchSocket.ensureConnection({
        gatewayBase,
        token,
      });

      if (!this.snapshotRefreshTimerId) {
        this.snapshotRefreshTimerId = setInterval(() => {
          this.requestRiders();
        }, 15000);
      }
    },

    handleTrackerSocketEvent(data) {
      if (!data || !data.type) {
        return;
      }

      switch (data.type) {
        case "tracker.snapshot":
          this.loading = false;
          this.applyRidersSnapshot((data.payload && data.payload.riders) || []);
          break;

        case "tracker.rider.updated":
          this.upsertRider(data.payload || {});
          break;

        case "auth.error":
        case "error":
          this.loading = false;
          console.error("Tracker gateway error:", data);
          break;

        default:
          break;
      }
    },

    formatDate(date) {
      if (!date) return "N/A";
      return new Date(date).toLocaleString();
    },

    formatFreshness(rider) {
      if (!rider) return "N/A";
      if (typeof rider.locationAgeSeconds !== "number") {
        return rider.locationStale ? "stale" : "fresh";
      }
      const threshold = rider.staleAfterSeconds || 60;
      const state = rider.locationStale ? "stale" : "fresh";
      return `${state}, ${rider.locationAgeSeconds}s old (limit ${threshold}s)`;
    },

    statusDisplay(status) {
      const s = this.normalizeStatus(status);
      if (s === "active") return "Available";
      if (s === "on_the_way") return "On Process";
      return "Off Duty";
    },

    createInfoWindow(rider) {
      const hasLat = typeof rider.lat === "number" && isFinite(rider.lat);
      const hasLng = typeof rider.lng === "number" && isFinite(rider.lng);
      const latText = hasLat ? rider.lat.toFixed(6) : "N/A";
      const lngText = hasLng ? rider.lng.toFixed(6) : "N/A";
      const last = rider.lastUpdated
        ? this.formatDate(rider.lastUpdated)
        : "N/A";
      const freshness = this.formatFreshness(rider);
      return new google.maps.InfoWindow({
        content: `
          <div class="info-window">
            <h4>${rider.name}</h4>
            <p><strong>ID:</strong> ${rider.riderId}</p>
          
            <p><strong>Location:</strong><br>
            Lat: ${latText}<br>
            Lng: ${lngText}</p>
            <p><strong>Last Updated:</strong><br>${last}</p>
            <p><strong>Freshness:</strong><br>${freshness}</p>
          </div>
        `,
      });
    },

    createCustomMarker(position, rider) {
      if (
        !position ||
        typeof position.lat !== "number" ||
        typeof position.lng !== "number"
      ) {
        return null;
      }
      const markerColor = this.getMarkerColor(rider.status);

      // Check if AdvancedMarkerElement is available
      if (google.maps.marker && google.maps.marker.AdvancedMarkerElement) {
        const content = document.createElement("div");
        content.className = "price-tag";
        content.style.backgroundColor = markerColor;
        content.textContent = rider.name || `Rider ${rider.riderId}`;

        return new google.maps.marker.AdvancedMarkerElement({
          position,
          content,
          map: this.map,
          title: rider.name || `Rider ${rider.riderId}`,
        });
      } else {
        // Fallback to regular marker
        return new google.maps.Marker({
          position,
          map: this.map,
          title: rider.name || `Rider ${rider.riderId}`,
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            fillColor: markerColor,
            fillOpacity: 1,
            strokeWeight: 1,
            scale: 8,
          },
        });
      }
    },

    updateRiderMarker(data) {
      const hasLat = typeof data.lat === "number" && isFinite(data.lat);
      const hasLng = typeof data.lng === "number" && isFinite(data.lng);
      const position =
        hasLat && hasLng ? { lat: data.lat, lng: data.lng } : null;

      if (this.markers.has(data.riderId)) {
        const marker = this.markers.get(data.riderId);
        if (position) {
          this.animateMarkerTo(marker, position);
        }

        // Update marker content if it exists (AdvancedMarkerElement), otherwise
        // update the regular marker icon/title as a fallback.
        const markerColor = this.getMarkerColor(data.status);

        if (marker && marker.content) {
          try {
            const content = marker.content;
            content.style.backgroundColor = markerColor;
            content.textContent = data.name || `Rider ${data.riderId}`;
          } catch (e) {
            // Defensive: if content isn't a DOM element or style access fails, fallback to icon update
            if (marker.setIcon) {
              marker.setIcon({
                path: google.maps.SymbolPath.CIRCLE,
                fillColor: markerColor,
                fillOpacity: 1,
                strokeWeight: 1,
                scale: 8,
              });
              if (marker.setTitle)
                marker.setTitle(data.name || `Rider ${data.riderId}`);
            }
          }
        } else if (marker && marker.setIcon) {
          // Regular google.maps.Marker fallback: change icon color and title
          marker.setIcon({
            path: google.maps.SymbolPath.CIRCLE,
            fillColor: markerColor,
            fillOpacity: 1,
            strokeWeight: 1,
            scale: 8,
          });

          if (marker.setTitle)
            marker.setTitle(data.name || `Rider ${data.riderId}`);
        }

        if (marker && marker.infoWindow) {
          const infoWindow = marker.infoWindow;
          infoWindow.setContent(this.createInfoWindow(data).getContent());
          if (marker.infoWindowOpen) {
            infoWindow.open(this.map, marker);
          }
        }
      } else {
        const marker = position
          ? this.createCustomMarker(position, data)
          : null;
        if (!marker) return; // don't create marker without valid coordinates
        const infoWindow = this.createInfoWindow(data);
        marker.infoWindow = infoWindow;
        marker.infoWindowOpen = false;

        marker.addListener("click", () => {
          this.markers.forEach((m) => {
            m.infoWindow.close();
            m.infoWindowOpen = false;
          });
          infoWindow.open(this.map, marker);
          marker.infoWindowOpen = true;
        });

        this.markers.set(data.riderId, marker);
      }
    },

    animateMarkerTo(marker, newPosition) {
      if (
        !newPosition ||
        typeof newPosition.lat !== "number" ||
        typeof newPosition.lng !== "number"
      )
        return;
      const frames = 20;

      // Check if marker is AdvancedMarkerElement
      const isAdvancedMarker =
        marker.constructor.name === "AdvancedMarkerElement";

      const start = {
        lat: isAdvancedMarker
          ? marker.position.lat
          : marker.getPosition().lat(),
        lng: isAdvancedMarker
          ? marker.position.lng
          : marker.getPosition().lng(),
      };

      // Skip if no movement
      if (
        Math.abs((newPosition.lat || 0) - (start.lat || 0)) < 1e-9 &&
        Math.abs((newPosition.lng || 0) - (start.lng || 0)) < 1e-9
      ) {
        return;
      }

      const deltaLat = (newPosition.lat - start.lat) / frames;
      const deltaLng = (newPosition.lng - start.lng) / frames;
      let frame = 0;

      if (marker.__animationFrameId) {
        cancelAnimationFrame(marker.__animationFrameId);
      }

      const animate = () => {
        frame++;
        const lat = start.lat + deltaLat * frame;
        const lng = start.lng + deltaLng * frame;

        if (isAdvancedMarker) {
          marker.position = { lat, lng };
        } else {
          marker.setPosition({ lat, lng });
        }

        if (frame < frames) {
          marker.__animationFrameId = requestAnimationFrame(animate);
        } else {
          marker.__animationFrameId = null;
        }
      };

      marker.__animationFrameId = requestAnimationFrame(animate);
    },

    showAddRiderModal() {
      this.isModalVisible = true;
    },

    closeModal() {
      this.isModalVisible = false;
      this.newRider = { riderId: "", name: "" };
    },

    addNewRider() {
      if (!this.newRider.riderId || !this.newRider.name) {
        alert("Please fill all fields");
        return;
      }

      if (!this.map || this.trackerSocketState !== "connected") {
        return;
      }

      const center = this.map.getCenter();
      realtimeDispatchSocket.sendEvent({
        type: "tracker.update",
        payload: {
          riderId: this.newRider.riderId,
          name: this.newRider.name,
          lat: center.lat(),
          lng: center.lng(),
          status: "active",
        },
      });

      this.closeModal();
    },

    // Helper to find rider index by riderId
    findRiderIndex(riderId) {
      return this.ridersList.findIndex(
        (r) => String(r.riderId) === String(riderId)
      );
    },

    // Upsert rider into ridersList reactively and update/create map marker
    upsertRider(incoming) {
      if (!incoming || !incoming.riderId) return;

      this.snapshotMissCounts.delete(String(incoming.riderId));

      const idx = this.findRiderIndex(incoming.riderId);

      // Prepare normalized rider object
      const riderObj = {
        riderId: String(incoming.riderId),
        name:
          incoming.name ||
          (idx !== -1 && this.ridersList[idx].name) ||
          `Rider ${incoming.riderId}`,
        lat:
          typeof incoming.lat === "number"
            ? incoming.lat
            : idx !== -1
            ? this.ridersList[idx].lat
            : null,
        lng:
          typeof incoming.lng === "number"
            ? incoming.lng
            : idx !== -1
            ? this.ridersList[idx].lng
            : null,
        status:
          incoming.status || (idx !== -1 ? this.ridersList[idx].status : null),
        lastUpdated:
          incoming.lastUpdated ||
          incoming.updatedAt ||
          (idx !== -1 ? this.ridersList[idx].lastUpdated : null),
        locationAgeSeconds:
          typeof incoming.locationAgeSeconds === "number"
            ? incoming.locationAgeSeconds
            : idx !== -1
            ? this.ridersList[idx].locationAgeSeconds
            : null,
        locationStale:
          typeof incoming.locationStale === "boolean"
            ? incoming.locationStale
            : idx !== -1
            ? this.ridersList[idx].locationStale
            : false,
        staleAfterSeconds:
          typeof incoming.staleAfterSeconds === "number"
            ? incoming.staleAfterSeconds
            : idx !== -1
            ? this.ridersList[idx].staleAfterSeconds
            : null,
      };

      if (idx === -1) {
        // Insert new rider
        this.ridersList.push(riderObj);
      } else {
        // Update existing rider reactively
        this.$set(
          this.ridersList,
          idx,
          Object.assign({}, this.ridersList[idx], riderObj)
        );
      }

      // Update or create marker on map
      try {
        this.updateRiderMarker(riderObj);
      } catch (e) {
        // Non-fatal: map may not be initialized yet
        // console.warn('updateRiderMarker failed', e);
      }

      if (
        this.selectedRiderInfo &&
        String(this.selectedRiderInfo.riderId) === riderObj.riderId
      ) {
        this.selectedRiderInfo = Object.assign(
          {},
          this.selectedRiderInfo,
          riderObj
        );
      }
    },

    getCurrentBbox() {
      if (!this.map) {
        return null;
      }

      const bounds = this.map.getBounds && this.map.getBounds();
      if (!bounds) {
        return null;
      }

      const southWest = bounds.getSouthWest();
      const northEast = bounds.getNorthEast();
      if (!southWest || !northEast) {
        return null;
      }

      return [
        southWest.lng(),
        southWest.lat(),
        northEast.lng(),
        northEast.lat(),
      ];
    },

    applyRidersSnapshot(riders) {
      const incomingIds = new Set(riders.map((rider) => String(rider.riderId)));
      const removableIds = new Set();

      // A live update and its Redis projection are asynchronous. Requiring
      // consecutive snapshot misses prevents a temporary empty/stale snapshot
      // from removing a marker that the live stream just updated.
      this.ridersList.forEach((rider) => {
        const riderId = String(rider.riderId);
        if (incomingIds.has(riderId)) {
          this.snapshotMissCounts.delete(riderId);
          return;
        }

        const misses = (this.snapshotMissCounts.get(riderId) || 0) + 1;
        this.snapshotMissCounts.set(riderId, misses);
        if (misses >= this.snapshotMissThreshold) {
          removableIds.add(riderId);
        }
      });

      this.ridersList
        .filter((rider) => removableIds.has(String(rider.riderId)))
        .forEach((rider) => {
          const marker = this.markers.get(String(rider.riderId));
          if (marker) {
            if (marker.__animationFrameId) {
              cancelAnimationFrame(marker.__animationFrameId);
            }
            if (marker.setMap) {
              marker.setMap(null);
            } else {
              marker.map = null;
            }
            this.markers.delete(String(rider.riderId));
          }
          this.snapshotMissCounts.delete(String(rider.riderId));
        });

      this.ridersList = this.ridersList.filter((rider) =>
        !removableIds.has(String(rider.riderId))
      );

      riders.forEach((rider) => {
        this.upsertRider(rider);
      });

      this.hasReceivedInitialSnapshot = true;
    },

    scheduleViewportRefresh() {
      if (this.viewportTimerId) {
        clearTimeout(this.viewportTimerId);
      }

      this.viewportTimerId = setTimeout(() => {
        this.requestRiders();
      }, 250);
    },

    subscribeViewport() {
      if (this.trackerSocketState !== "connected") {
        return;
      }

      const bbox = this.getCurrentBbox();
      if (!bbox) {
        return;
      }

      realtimeDispatchSocket.sendEvent({
        type: "tracker.viewport.subscribe",
        payload: { bbox },
      });
    },

    requestRiders(options = {}) {
      if (this.trackerSocketState !== "connected") {
        return;
      }

      if (options.showLoading || !this.hasReceivedInitialSnapshot) {
        this.loading = true;
      }
      const bbox = options.forceFullSnapshot ? null : this.getCurrentBbox();
      realtimeDispatchSocket.sendEvent({
        type: "tracker.snapshot.request",
        payload: bbox ? { bbox } : {},
      });
      if (!options.forceFullSnapshot) {
        this.subscribeViewport();
      }
    },

    setCenter(location) {
      const coords = this.provinceCoordinates[location];
      if (coords && this.map) {
        this.map.setCenter(coords);
        this.map.setZoom(coords.zoom);
        this.selectedProvince = location;
      }
    },

    handleProvinceChange(selectedValue) {
      if (selectedValue) {
        // Province selected - zoom to that province
        this.setCenter(selectedValue);
      } else {
        // Selection cleared - reset to Thailand overview
        this.resetMapToThailand();
      }
    },

    resetMapToThailand() {
      if (this.map) {
        const thailandCenter = this.locations.thailand;
        this.map.setCenter(thailandCenter);
        this.map.setZoom(thailandCenter.zoom);
        this.selectedProvince = null;
      }
    },

    refreshRiders() {
      this.requestRiders({ showLoading: true });
    },

    warpToRider(riderId) {
      if (!riderId) {
        this.selectedRiderInfo = null;
        return;
      }

      const rider = this.ridersList.find((r) => r.riderId === riderId);
      if (rider && this.map) {
        // Set selected riderId for update-location click handler
        this.selectedRiderId = riderId;
        // Set rider info for display
        this.selectedRiderInfo = rider;

        // Center map on rider location
        const riderPosition = { lat: rider.lat, lng: rider.lng };
        this.map.setCenter(riderPosition);
        this.map.setZoom(15); // Close zoom for individual rider

        // Clear province selection since we're focusing on a rider
        this.selectedProvince = null;

        // Open the rider's info window
        const marker = this.markers.get(riderId);
        if (marker && marker.infoWindow) {
          // Close all other info windows
          this.markers.forEach((m) => {
            m.infoWindow.close();
            m.infoWindowOpen = false;
          });

          // Open this rider's info window
          marker.infoWindow.open(this.map, marker);
          marker.infoWindowOpen = true;
        }
      }
    },

    getRiderStatusColor(status) {
      const normalizedStatus = this.normalizeStatus(status);
      switch (normalizedStatus) {
        case "active":
          return "green";
        case "on_the_way":
          return "orange";
        default:
          return "red";
      }
    },

    getRiderStatusIcon(status) {
      const normalizedStatus = this.normalizeStatus(status);
      switch (normalizedStatus) {
        case "active":
          return "radio_button_checked";
        case "on_the_way":
          return "schedule";
        default:
          return "radio_button_unchecked";
      }
    },

    getMarkerColor(status) {
      const normalizedStatus = this.normalizeStatus(status);
      switch (normalizedStatus) {
        case "active":
          return "#4CAF50"; // Green
        case "on_the_way":
          return "#FF9800"; // Orange
        default:
          return "#f44336"; // Red
      }
    },
  },

  watch: {
    // Add watcher for route changes
    "$route.params.province"(newProvince) {
      if (newProvince && this.map) {
        const province = newProvince.toLowerCase();
        const coordinates = this.provinceCoordinates[province];
        if (coordinates) {
          this.map.setCenter(coordinates);
          this.map.setZoom(coordinates.zoom);
        }
      }
    },
  },
};
</script>

<style scoped>
/* Custom overrides for Vuetify components */
.v-card {
  border-radius: 16px !important;
}

.v-list {
  border-radius: 16px !important;
}

.v-card__title {
  font-size: 16px !important;
}

.v-chip {
  font-size: 12px;
}

.v-list__tile__title {
  font-size: 14px;
}

/* Map styles */
#map {
  border-radius: 0 0 4px 4px;
}

/* Marker styles */
.price-tag {
  background-color: #4285f4;
  border-radius: 8px;
  color: #ffffff;
  font-size: 14px;
  padding: 10px 15px;
  position: relative;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  font-weight: 500;
}

.price-tag::after {
  content: "";
  position: absolute;
  left: 50%;
  top: 100%;
  transform: translate(-50%, 0);
  width: 0;
  height: 0;
  border-left: 8px solid transparent;
  border-right: 8px solid transparent;
  border-top: 8px solid #4285f4;
}

.price-tag[style*="background-color: #4CAF50"]::after {
  border-top-color: #4caf50;
}

.price-tag[style*="background-color: #FF9800"]::after {
  border-top-color: #ff9800;
}

.price-tag[style*="background-color: #f44336"]::after {
  border-top-color: #f44336;
}

/* Info window custom styles */
.info-window {
  max-width: 250px;
  padding: 8px;
}

.info-window h4 {
  margin: 0 0 8px 0;
  color: #1976d2;
  font-size: 16px;
}

.info-window p {
  margin: 4px 0;
  font-size: 13px;
  line-height: 1.4;
}

/* Responsive adjustments */
@media (max-width: 960px) {
  .v-card__title {
    padding: 12px 16px;
  }

  .v-card__text {
    padding: 12px 16px;
  }
}

/* Loading overlay customization */
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.9);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 10;
}

/* Breadcrumb styling */
.v-breadcrumbs {
  padding: 0;
}

.v-breadcrumbs__item {
  font-size: 13px;
}

/* Province button styling */
.text-capitalize {
  text-transform: capitalize !important;
}

/* Statistics card styling */
.v-list--dense .v-list__tile {
  height: auto;
  min-height: 40px;
  padding: 8px 16px;
}

.v-chip--small {
  height: 24px;
  font-size: 11px;
}
</style>
